import React, { useCallback, useRef, useState } from 'react';
import { Title, usePermissions } from 'react-admin';
import {
    Box,
    Card,
    CardContent,
    Chip,
    CircularProgress,
    Dialog,
    DialogActions,
    DialogContent,
    DialogContentText,
    DialogTitle,
    Divider,
    Paper,
    Typography,
} from '@mui/material';
import SyncIcon from '@mui/icons-material/Sync';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import InfoIcon from '@mui/icons-material/Info';
import WarningIcon from '@mui/icons-material/Warning';
import StorageIcon from '@mui/icons-material/Storage';
import DeleteForeverIcon from '@mui/icons-material/DeleteForever';
import FolderDeleteIcon from '@mui/icons-material/FolderDelete';
import { ButtonField, Tag } from '@wk/components-v3-react16';
import { JumpstartButton, JumpstartFilterText } from '../components/JumpstartInputs';
import { API_URL } from '../config';
import { useT } from '../i18n/useT';

// ─── types ────────────────────────────────────────────────────────────────────

type EventType = 'info' | 'warning' | 'error' | 'success' | 'done';

interface SyncEvent {
    id: number;
    type: EventType;
    message: string;
    created?: number;
    updated?: number;
    errors?: number;
    total?: number;
}

interface SyncStats {
    created: number;
    updated: number;
    errors: number;
    total: number;
}

// ─── icon helpers ─────────────────────────────────────────────────────────────

const eventIcon = (type: EventType) => {
    switch (type) {
        case 'success': return <CheckCircleIcon fontSize="small" sx={{ color: 'success.main' }} />;
        case 'error':   return <ErrorIcon fontSize="small" sx={{ color: 'error.main' }} />;
        case 'warning': return <WarningIcon fontSize="small" sx={{ color: 'warning.main' }} />;
        case 'done':    return <CheckCircleIcon fontSize="small" sx={{ color: 'text.disabled' }} />;
        default:        return <InfoIcon fontSize="small" sx={{ color: 'info.main' }} />;
    }
};

const eventColor = (type: EventType): string => {
    switch (type) {
        case 'success': return '#e8f5e9';
        case 'error':   return '#ffebee';
        case 'warning': return '#fff8e1';
        default:        return 'transparent';
    }
};

// ─── component ────────────────────────────────────────────────────────────────

export const CatalogSync = () => {
    const t = useT();
    const { permissions } = usePermissions();
    const [running, setRunning] = useState(false);
    const [events, setEvents] = useState<SyncEvent[]>([]);
    const [stats, setStats] = useState<SyncStats | null>(null);
    const [finished, setFinished] = useState(false);
    const logEndRef = useRef<HTMLDivElement>(null);
    const esRef = useRef<EventSource | null>(null);
    let idCounter = useRef(0);

    const appendEvent = useCallback((evt: Omit<SyncEvent, 'id'>) => {
        const newEvt: SyncEvent = { ...evt, id: ++idCounter.current };
        setEvents(prev => [...prev, newEvt]);
        // Auto-scroll to bottom
        setTimeout(() => logEndRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }, []);

    const startSync = useCallback(() => {
        if (running) return;

        // Reset state
        setEvents([]);
        setStats(null);
        setFinished(false);
        setRunning(true);

        const token = localStorage.getItem('token');
        if (!token) {
            appendEvent({ type: 'error', message: t('catalog.sync.events.tokenMissing') });
            setRunning(false);
            setFinished(true);
            return;
        }

        const url = `${API_URL}/catalog/sync/stream?token=${encodeURIComponent(token)}`;
        const es = new EventSource(url);
        esRef.current = es;

        es.onmessage = (e) => {
            try {
                const data = JSON.parse(e.data) as SyncEvent & { type: EventType };
                if (data.type === 'done') {
                    es.close();
                    esRef.current = null;
                    setRunning(false);
                    setFinished(true);
                    return;
                }
                if (data.type === 'success' && data.total !== undefined) {
                    setStats({
                        created: data.created ?? 0,
                        updated: data.updated ?? 0,
                        errors: data.errors ?? 0,
                        total: data.total ?? 0,
                    });
                }
                appendEvent(data);
            } catch {
                // ignore parse errors
            }
        };

        es.onerror = () => {
            appendEvent({ type: 'error', message: t('catalog.sync.events.sseInterrupted') });
            es.close();
            esRef.current = null;
            setRunning(false);
            setFinished(true);
        };
    }, [running, appendEvent, t]);

    if (permissions !== 'admin') {
        return (
            <Box p={3}>
                <Typography variant="h6" color="error">
                    {t('catalog.permissionDenied')}
                </Typography>
            </Box>
        );
    }

    return (
        <Box>
            <Title title={t('catalog.title')} />

            {/* ── Header card ── */}
            <Card sx={{ mb: 3 }}>
                <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <StorageIcon color="primary" fontSize="large" />
                        <Box>
                            <Typography variant="h5" fontWeight={600}>
                                {t('catalog.sync.heading')}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {t('catalog.sync.description')}
                            </Typography>
                        </Box>
                    </Box>

                    <Divider sx={{ my: 2 }} />

                    <ButtonField type="primary" size="large">
                        <button type="button" onClick={startSync} disabled={running}>
                            {running ? t('catalog.sync.actionRunningLabel') : t('catalog.sync.actionLabel')}
                        </button>
                    </ButtonField>
                </CardContent>
            </Card>

            {/* ── Stats chips (shown after success) ── */}
            {stats && (
                <Card sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="subtitle1" fontWeight={600} mb={1.5}>
                            {t('catalog.sync.resultsTitle')}
                        </Typography>
                        <Box display="flex" gap={1} flexWrap="wrap">
                            <Tag type="light" color="strong">{t('catalog.sync.stats.total', { value: stats.total })}</Tag>
                            <Tag type="light" color="success">{t('catalog.sync.stats.created', { value: stats.created })}</Tag>
                            <Tag type="light" color="info">{t('catalog.sync.stats.updated', { value: stats.updated })}</Tag>
                            <Tag type="light" color={stats.errors > 0 ? 'danger' : 'strong'}>{t('catalog.sync.stats.errors', { value: stats.errors })}</Tag>
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* ── Danger Zone ── */}
            <DangerZone />

            {/* ── Log area ── */}
            {(events.length > 0 || running) && (
                <Card>
                    <CardContent>
                        <Box display="flex" alignItems="center" justifyContent="space-between" mb={1}>
                            <Typography variant="subtitle1" fontWeight={600}>
                                {t('catalog.sync.progressTitle')}
                            </Typography>
                            {running && <CircularProgress size={18} />}
                            {finished && !running && (
                                <Tag type="light" color="success">{t('catalog.sync.completed')}</Tag>
                            )}
                        </Box>
                        <Paper
                            variant="outlined"
                            sx={{
                                maxHeight: 480,
                                overflowY: 'auto',
                                p: 1,
                                backgroundColor: '#fafafa',
                                fontFamily: 'monospace',
                                fontSize: '0.82rem',
                            }}
                        >
                            {events.map((evt) => (
                                <Box
                                    key={evt.id}
                                    display="flex"
                                    alignItems="flex-start"
                                    gap={0.8}
                                    px={0.5}
                                    py={0.4}
                                    sx={{
                                        backgroundColor: eventColor(evt.type),
                                        borderRadius: 0.5,
                                        mb: 0.2,
                                    }}
                                >
                                    <Box mt={0.1} flexShrink={0}>{eventIcon(evt.type)}</Box>
                                    <Typography
                                        variant="body2"
                                        sx={{ fontFamily: 'monospace', fontSize: '0.82rem', wordBreak: 'break-word' }}
                                    >
                                        {evt.message}
                                    </Typography>
                                </Box>
                            ))}
                            <div ref={logEndRef} />
                        </Paper>
                    </CardContent>
                </Card>
            )}
        </Box>
    );
};

// ─── Danger Zone ─────────────────────────────────────────────────────────────

interface DangerActionResult {
    status: string;
    message: string;
    [key: string]: unknown;
}

const CONFIRM_WORD = 'DELETE';

interface DangerCardProps {
    title: string;
    description: string;
    icon: React.ReactNode;
    endpoint: string;
    method?: string;
    buttonLabel: string;
    onDone?: (result: DangerActionResult) => void;
}

const DangerCard: React.FC<DangerCardProps> = ({
    title, description, icon, endpoint, method = 'DELETE', buttonLabel, onDone,
}) => {
    const t = useT();
    const [dialogOpen, setDialogOpen] = useState(false);
    const [confirmText, setConfirmText] = useState('');
    const [loading, setLoading] = useState(false);
    const [result, setResult] = useState<DangerActionResult | null>(null);

    const handleOpen = () => { setConfirmText(''); setResult(null); setDialogOpen(true); };
    const handleClose = () => { if (!loading) setDialogOpen(false); };

    const handleConfirm = async () => {
        if (confirmText !== CONFIRM_WORD) return;
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const resp = await fetch(`${API_URL}${endpoint}`, {
                method,
                headers: {
                    Authorization: `Bearer ${token}`,
                    'Content-Type': 'application/json',
                },
            });
            const data: DangerActionResult = await resp.json();
            setResult(data);
            onDone?.(data);
        } catch (e: unknown) {
            setResult({ status: 'error', message: String(e) });
        } finally {
            setLoading(false);
            setDialogOpen(false);
        }
    };

    const confirmed = confirmText === CONFIRM_WORD;

    return (
        <>
            <Card
                sx={{
                    border: '1.5px solid',
                    borderColor: 'error.light',
                    mb: 2,
                }}
            >
                <CardContent>
                    <Box display="flex" alignItems="center" gap={2} mb={1}>
                        <Box sx={{ color: 'error.main' }}>{icon}</Box>
                        <Box flex={1}>
                            <Typography variant="h6" color="error" fontWeight={600}>
                                {title}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                                {description}
                            </Typography>
                        </Box>
                        <JumpstartButton
                            label={buttonLabel}
                            color="danger"
                            onClick={handleOpen}
                            startIcon={<DeleteForeverIcon fontSize="small" />}
                        />
                    </Box>
                    {result && (
                        <Box
                            mt={1}
                            p={1.5}
                            borderRadius={1}
                            sx={{
                                backgroundColor: result.status === 'ok' ? '#e8f5e9' : '#ffebee',
                                fontFamily: 'monospace',
                                fontSize: '0.82rem',
                            }}
                        >
                            {result.message}
                        </Box>
                    )}
                </CardContent>
            </Card>

            <Dialog open={dialogOpen} onClose={handleClose} maxWidth="sm" fullWidth>
                <DialogTitle sx={{ color: 'error.main', fontWeight: 700 }}>
                    {t('catalog.danger.cannotBeUndone')}
                </DialogTitle>
                <DialogContent>
                    <DialogContentText mb={2}>
                        {description}
                        <br /><br />
                        {`Type `}<strong>{CONFIRM_WORD}</strong>{` ${t('catalog.danger.confirmPromptSuffix')}`}
                    </DialogContentText>
                    <JumpstartFilterText
                        label={t('catalog.danger.confirmInputLabel', { word: CONFIRM_WORD })}
                        value={confirmText}
                        onChange={setConfirmText}
                        onEnter={() => { if (confirmed) handleConfirm(); }}
                        fullWidth
                        autoFocus
                        disabled={loading}
                        errorText={
                            confirmText.length > 0 && !confirmed
                                ? t('catalog.danger.confirmInputError', { word: CONFIRM_WORD })
                                : undefined
                        }
                    />
                </DialogContent>
                <DialogActions sx={{ px: 3, pb: 2 }}>
                    <JumpstartButton label={t('catalog.danger.cancel')} type="secondary" onClick={handleClose} disabled={loading} />
                    <JumpstartButton
                        label={loading ? t('catalog.danger.deleting') : t('catalog.danger.confirmDelete')}
                        color="danger"
                        onClick={handleConfirm}
                        disabled={!confirmed || loading}
                        startIcon={loading
                            ? <CircularProgress size={16} color="inherit" />
                            : <DeleteForeverIcon fontSize="small" />}
                    />
                </DialogActions>
            </Dialog>
        </>
    );
};

const DangerZone: React.FC = () => {
    const t = useT();
    return (
        <Box mt={4}>
            <Typography
                variant="h6"
                fontWeight={700}
                color="error"
                mb={2}
                sx={{ textTransform: 'uppercase', letterSpacing: 1 }}
            >
                {t('catalog.danger.sectionTitle')}
            </Typography>
            <DangerCard
                title={t('catalog.danger.allFiles.title')}
                description={t('catalog.danger.allFiles.description')}
                icon={<FolderDeleteIcon fontSize="large" />}
                endpoint="/catalog/all-files"
                buttonLabel={t('catalog.danger.allFiles.buttonLabel')}
            />
            <DangerCard
                title={t('catalog.danger.allPublications.title')}
                description={t('catalog.danger.allPublications.description')}
                icon={<DeleteForeverIcon fontSize="large" />}
                endpoint="/catalog/all-publications"
                buttonLabel={t('catalog.danger.allPublications.buttonLabel')}
            />
        </Box>
    );
};

export const catalogIcon = <StorageIcon />;
